import { useFormikContext } from 'formik';
import diff, { DifferenceCreate } from 'microdiff';
import { memo, useCallback, useEffect, useState } from 'react';

import { useSyncFormField } from '@features/Remote/Interaction/useInteractions';
import { useDebounce } from '@hooks/useDebounce';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';

import { filter, filterValuePlaceholders } from './filter';
import { FormValueDiff } from './types';

const sanitizeValues = (value: string | number | boolean): string | number => {
  if (typeof value === 'boolean') return String(value);
  return value;
};

// stable reference so an omitted prop doesn't create a new array every
// render, which would defeat the effect dependency array downstream
const EMPTY_IGNORE_KEYS: string[] = [];

interface FormSyncProps {
  committedValues?: Record<string, unknown>;
  emptyFormIgnoreKeys?: string[];
}

const FormSyncGate = ({
  committedValues,
  emptyFormIgnoreKeys,
}: FormSyncProps) => {
  const { isSyncedAppointment } = useAppointmentContext();

  if (!isSyncedAppointment) {
    return null;
  }

  return (
    <FormSync
      committedValues={committedValues}
      emptyFormIgnoreKeys={emptyFormIgnoreKeys}
    />
  );
};

const FormSync = <FormValuesT,>({
  committedValues,
  emptyFormIgnoreKeys = EMPTY_IGNORE_KEYS,
}: FormSyncProps) => {
  const { values } = useFormikContext<FormValuesT>();
  const [prevValues, setPrevValues] = useState<FormValuesT>(values);
  const syncFormField = useSyncFormField(committedValues, emptyFormIgnoreKeys);

  const calcDiff = useCallback(
    (values: FormValuesT, prevValues: FormValuesT) => {
      const formDiff = diff(
        prevValues as Record<string, string>,
        values as Record<string, string>
      );

      setPrevValues(values);
      if (formDiff.length > 0) {
        const sanitizedDiff: FormValueDiff = formDiff
          .map((valueDiff) => {
            const path = valueDiff.path.join('.');

            if (valueDiff.type === 'REMOVE') {
              return { path, val: undefined, type: valueDiff.type };
            }

            if (filter.includes(path)) {
              const filteredValue = filterValuePlaceholders[path];
              if (filteredValue) {
                return { path, val: filteredValue, type: valueDiff.type };
              }
              return null;
            }

            const val = (valueDiff as DifferenceCreate)?.value ?? '';
            return { path, val: sanitizeValues(val), type: valueDiff.type };
          })
          // filter out empty values, which would be fields we do not want to
          // update on the receiving client at all.
          .filter((diffItem) => Boolean(diffItem)) as FormValueDiff;

        syncFormField(sanitizedDiff);
      }
    },
    [syncFormField]
  );

  const debouncedCalcDiff = useDebounce(calcDiff, 250);

  useEffect(() => {
    debouncedCalcDiff(values, prevValues);
  }, [values, prevValues, debouncedCalcDiff]);

  return null;
};

const FormSyncMemo = memo(FormSyncGate);
export { FormSyncMemo as FormSync };
